import { useEffect, useState } from 'react';

// AVERTISSEMENT « VISAGE PRES DU PLI » (correctif 5, 2026-10-16) — seulement quand un visage est REELLEMENT
// detecte pres de la reliure, jamais en permanence.
//
// Detection : API native FaceDetector du navigateur (Shape Detection), quand elle existe. Sans elle (c'est le cas
// de beaucoup de navigateurs), on ne detecte rien et on n'affiche donc rien : mieux vaut aucun avertissement
// qu'un avertissement general que tout le monde apprend a ignorer.

/**
 * Partie de la photo VISIBLE dans son cadre, en coordonnees normalisees de l'image (0..1), pour le mode
 * « Remplir » : la photo est dessinee en `cover` avec object-position fx/fy, puis zoomee autour de ce meme
 * point (meme maths que framing.panFocus).
 */
export function visibleWindow({ imageRatio, frameRatio, focusX = 50, focusY = 50, zoom = 1 }) {
  if (!(imageRatio > 0) || !(frameRatio > 0)) return null;
  const fw = frameRatio;
  const fh = 1;
  const iw = imageRatio;
  const ih = 1;
  const s = Math.max(fw / iw, fh / ih);
  const fx = focusX / 100;
  const fy = focusY / 100;
  const x0 = fx * (fw - s * iw);
  const y0 = fy * (fh - s * ih);
  const z = zoom > 0 ? zoom : 1;
  const ox = fx * fw;
  const oy = fy * fh;
  const toImage = (p, origin, start, size) => ((origin + (p - origin) / z) - start) / size;
  return {
    u0: toImage(0, ox, x0, s * iw),
    u1: toImage(fw, ox, x0, s * iw),
    v0: toImage(0, oy, y0, s * ih),
    v1: toImage(fh, oy, y0, s * ih)
  };
}

/**
 * Un des visages (boites normalisees { x, y, w, h }, 0..1 dans l'image) tombe-t-il dans la bande du pli ?
 * `foldFraction` : largeur de la bande en part de la largeur de la page ; `spreadSide` : 'left' (le pli est a
 * DROITE de cette page) ou 'right' (le pli est a gauche).
 */
export function faceNearFold({ faces, window: win, spreadSide, foldFraction }) {
  if (!win || !faces || !faces.length || !(foldFraction > 0)) return false;
  const width = win.u1 - win.u0;
  if (!(width > 0)) return false;
  return faces.some((face) => {
    const left = (face.x - win.u0) / width; // 0..1 dans le cadre
    const right = (face.x + face.w - win.u0) / width;
    if (right <= 0 || left >= 1) return false; // visage hors cadre : rien a signaler
    return spreadSide === 'left' ? right > 1 - foldFraction : left < foldFraction;
  });
}

const detectionCache = new Map();

async function detectFaces(url) {
  if (detectionCache.has(url)) return detectionCache.get(url);
  const result = (async () => {
    if (typeof window === 'undefined' || typeof window.FaceDetector !== 'function' || !url) return null;
    const image = await new Promise((resolve, reject) => {
      const element = new Image();
      element.crossOrigin = 'anonymous';
      element.onload = () => resolve(element);
      element.onerror = reject;
      element.src = url;
    });
    const detector = new window.FaceDetector({ fastMode: true, maxDetectedFaces: 8 });
    const found = await detector.detect(image);
    const w = image.naturalWidth || 1;
    const h = image.naturalHeight || 1;
    return found.map((entry) => ({
      x: entry.boundingBox.x / w,
      y: entry.boundingBox.y / h,
      w: entry.boundingBox.width / w,
      h: entry.boundingBox.height / h
    }));
  })().catch(() => null);
  detectionCache.set(url, result);
  return result;
}

/** true seulement si un visage detecte tombe dans la bande du pli pour le cadrage COURANT. */
export function useFaceNearFold({ enabled, url, imageRatio, frameRatio, framing, spreadSide, foldFraction }) {
  const [faces, setFaces] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setFaces(null);
    if (enabled && url) detectFaces(url).then((result) => { if (!cancelled) setFaces(result); });
    return () => { cancelled = true; };
  }, [enabled, url]);
  if (!enabled || !faces || framing.fit !== 'cover') return false;
  const win = visibleWindow({ imageRatio, frameRatio, focusX: framing.focusX, focusY: framing.focusY, zoom: framing.zoom });
  return faceNearFold({ faces, window: win, spreadSide, foldFraction });
}
